import React, { useState, useContext } from "react";
import {
  Box,
  Avatar,
  Typography,
  Stack,
  Chip,
  useTheme,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ButtonBase,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
  FormControl,
  Select,
} from "@mui/material";
import {
  LogOut,
  Moon,
  Sun,
  Timer,
  AlertTriangle,
  ChevronDown
} from "lucide-react";
import { GB, FR, ES, PT, DE, NL, type FlagComponent } from "country-flag-icons/react/3x2";
import { useLogout, useGetLocale, useSetLocale, useTranslate } from "@refinedev/core";
import { useAuth } from "../../contexts/auth-context";
import { ColorModeContext } from "../../contexts/color-mode";
import i18n from "../../I18n";
import { useNavigate } from "react-router-dom";
import { getTrialDaysRemaining, isTrialExpired } from "../../utils/trial-permissions";

// Liste des langues supportées (doit correspondre à I18n.ts)
const SUPPORTED_LANGUAGES = ["en", "fr", "es", "pt", "de", "nl"];

// Mapping langue -> composant drapeau
const flagComponents: Record<string, FlagComponent> = {
  en: GB,
  fr: FR,
  es: ES,
  pt: PT,
  de: DE,
  nl: NL,
};

// Mapping langue -> nom d'affichage (court)
const languageNames: Record<string, string> = {
  en: "EN",
  fr: "FR",
  es: "ES",
  pt: "PT",
  de: "DE",
  nl: "NL",
};

// Mapping langue -> nom d'affichage (complet)
const languageFullNames: Record<string, string> = {
  en: "English",
  fr: "Français",
  es: "Español",
  pt: "Português",
  de: "Deutsch",
  nl: "Nederlands",
};

interface SiderFooterProps {
  collapsed?: boolean;
}

export const SiderFooter: React.FC<SiderFooterProps> = ({ collapsed = false }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const { mutate: logout } = useLogout();
  const [open, setOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const translate = useTranslate();

  // Color mode context
  const { mode, setMode } = useContext(ColorModeContext);

  // Language context
  const changeLanguage = useSetLocale();
  const locale = useGetLocale();
  const currentLocale = locale();

  const handleClick = () => {
    setOpen(!open);
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    logout();
    setLogoutDialogOpen(false);
    setOpen(false);
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'error';
      case 'MODERATOR':
        return 'warning';
      case 'SAFEE_MODERATOR':
        return 'warning';
      case 'USER':
      default:
        return 'primary';
    }
  };

  if (!user) return null;

  // Trial info for MODERATOR_FREE users
  const daysRemaining = user.role === 'MODERATOR_FREE'
    ? getTrialDaysRemaining(user.freeTrialStartedAt || null)
    : 0;
  const isExpired = user.role === 'MODERATOR_FREE'
    ? isTrialExpired(user.freeTrialStartedAt || null)
    : false;

  const getTrialChipProps = () => {
    if (isExpired) {
      return {
        color: 'error' as const,
        icon: <AlertTriangle size={16} />,
        label: translate('trial.expired')
      };
    } else if (daysRemaining <= 3) {
      return {
        color: 'warning' as const,
        icon: <Timer size={16} />,
        label: translate('trial.daysRemainingShort', { days: daysRemaining })
      };
    } else {
      return {
        color: 'info' as const,
        icon: <Timer size={16} />,
        label: translate('trial.daysRemaining', { days: daysRemaining })
      };
    }
  };

  return (
    <Box>
      {/* Trial Chip for MODERATOR_FREE users */}
      {user.role === 'MODERATOR_FREE' && (
        <Box sx={{
          p: collapsed ? 1 : 2,
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: theme.palette.mode === "dark"
            ? theme.palette.grey[900]
            : theme.palette.grey[50]
        }}>
          <Tooltip
            title={
              isExpired
                ? translate('trial.expiredTooltip')
                : translate('trial.remainingTooltip', { days: daysRemaining })
            }
            placement="right"
          >
            <Chip
              {...getTrialChipProps()}
              onClick={() => navigate('/pricing')}
              clickable
              sx={{
                width: '100%',
                fontWeight: 'bold',
                fontSize: collapsed ? '0.75rem' : '0.875rem',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: 2,
                },
                transition: 'all 0.3s ease',
                animation: daysRemaining <= 3 ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                  '100%': { transform: 'scale(1)' },
                },
              }}
            />
          </Tooltip>
        </Box>
      )}

      {/* User Profile Section */}
      <ButtonBase
        onClick={handleClick}
        sx={{
          width: "100%",
          pt: collapsed ? 1 : 2,
          px: collapsed ? 1 : 2,
          pb: collapsed ? 0.5 : 1,
          borderBottom: 1,
          borderColor: "divider",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: theme.palette.mode === "dark"
              ? theme.palette.grey[800]
              : theme.palette.grey[50],
          },
          // Sur mobile, supprimer la bordure en bas car c'est le dernier élément visible
          '@media (max-width: 768px)': {
            borderBottom: 'none',
          }
        }}
      >
        {collapsed ? (
          // Mode collapsed : afficher seulement l'avatar avec chevron
          <Tooltip title={user.fullname || user.email} placement="right">
            <Stack alignItems="center" spacing={0.5}>
              <Avatar
                src={user.profilePicture}
                alt={user.fullname || user.email}
                sx={{ width: 32, height: 32 }}
              />
              <ChevronDown
                size={14}
                style={{
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  color: theme.palette.text.secondary,
                }}
              />
            </Stack>
          </Tooltip>
        ) : (
          // Mode normal : afficher le profil complet avec chevron centré en dessous
          <Stack alignItems="center" sx={{ width: "100%" }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%" }}>
              <Avatar
                src={user.profilePicture}
                alt={user.fullname || user.email}
                sx={{ width: 40, height: 40 }}
              />
              <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    lineHeight: 1.2,
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {user.fullname || user.email}
                </Typography>
                {user.role && (
                  <Chip
                    label={user.role}
                    size="small"
                    color={getRoleColor(user.role)}
                    sx={{
                      height: 18,
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      mt: 0.5,
                    }}
                  />
                )}
              </Box>
            </Stack>
            <ChevronDown
              size={18}
              style={{
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
                color: theme.palette.text.secondary,
                marginTop: 4,
              }}
            />
          </Stack>
        )}
      </ButtonBase>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <List
          sx={{
            padding: "0 !important",
            paddingTop: "0 !important",
            mt: 0,
            backgroundColor: theme.palette.mode === "dark"
              ? theme.palette.grey[900]
              : theme.palette.background.paper,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.4)'
              : '0 8px 32px rgba(0, 0, 0, 0.12)',
          }}
        >
          {/* Dark Mode Toggle */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => setMode()}
              sx={{
                '& .MuiListItemIcon-root': {
                  minWidth: '40px',
                },
              }}
            >
              <ListItemIcon>
                {mode === "dark" ? <Sun size={18} /> : <Moon size={18} />}
              </ListItemIcon>
              <ListItemText>
                {mode === "dark" ? translate('settings.lightMode', 'Mode clair') : translate('settings.darkMode', 'Mode sombre')}
              </ListItemText>
            </ListItemButton>
          </ListItem>

          {/* Language Selection */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={(e) => setLanguageMenuAnchor(e.currentTarget)}
              sx={{
                '& .MuiListItemIcon-root': {
                  minWidth: '40px',
                },
              }}
            >
              <ListItemIcon>
                {(() => {
                  const FlagComponent = flagComponents[currentLocale || 'en'];
                  return FlagComponent ? (
                    <FlagComponent style={{ width: 22, height: 16, borderRadius: 2 }} />
                  ) : (
                    <Box sx={{ width: 22, height: 16, backgroundColor: 'grey.300', borderRadius: 0.5 }} />
                  );
                })()}
              </ListItemIcon>
              <ListItemText>{translate('settings.language', 'Langue')}</ListItemText>
            </ListItemButton>
          </ListItem>

          <Divider />

          {/* Logout */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogoutClick}
              sx={{
                color: 'error.main',
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: 'error.main',
                  color: 'error.contrastText',
                },
                '& .MuiListItemIcon-root': {
                  minWidth: '40px',
                },
              }}
            >
              <ListItemIcon>
                <LogOut size={18} style={{ color: 'inherit' }} />
              </ListItemIcon>
              <ListItemText>{translate('sidebar.logout')}</ListItemText>
            </ListItemButton>
          </ListItem>
        </List>

        {/* Language Menu */}
        <Menu
          anchorEl={languageMenuAnchor}
          open={Boolean(languageMenuAnchor)}
          onClose={() => setLanguageMenuAnchor(null)}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        >
          {SUPPORTED_LANGUAGES.map((lang: string) => {
            const FlagComponent = flagComponents[lang];
            return (
              <MenuItem
                key={lang}
                selected={lang === currentLocale}
                onClick={() => {
                  changeLanguage(lang);
                  setLanguageMenuAnchor(null);
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  {FlagComponent ? (
                    <FlagComponent style={{ width: 22, height: 16, borderRadius: 2 }} />
                  ) : (
                    <Box sx={{ width: 22, height: 16, backgroundColor: 'grey.300', borderRadius: 0.5 }} />
                  )}
                  <Typography variant="body2">
                    {languageFullNames[lang] || lang.toUpperCase()}
                  </Typography>
                </Stack>
              </MenuItem>
            );
          })}
        </Menu>
      </Collapse>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">
          {translate('sidebar.logout.confirm.title', 'Déconnexion')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            {translate('sidebar.logout.confirm.message', 'Êtes-vous sûr de vouloir vous déconnecter ?')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="inherit">
            {translate('common.cancel', 'Annuler')}
          </Button>
          <Button onClick={handleLogoutConfirm} color="error" variant="contained" autoFocus>
            {translate('sidebar.logout', 'Déconnexion')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
